import {
  LazyQueryExecFunction,
  OperationVariables,
  useMutation,
  useReactiveVar,
} from '@apollo/client';
import {
  DefaultButton,
  IDropdownOption,
  MessageBar,
  MessageBarType,
  PrimaryButton,
  Separator,
  Stack,
  Text,
  useTheme,
} from '@fluentui/react';
import { cutToast } from 'utility/toast';
import { useEffect, useState } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { CompanyConfigurationCategories_companyConfigurationCategories_nodes } from '../__generated__/CompanyConfigurationCategories';

import { AddRuleModal } from './AddRuleModal';
import BUSINESS_CATEGORY_REFRESH from './CompanyConfigurationCategory.graphql';
import FINALIZE_PROCESS from './CompanyConfigurationFinalizationProcess.graphql';

import { Prompt } from 'react-router-dom';
import { useSubmitForm } from '../hooks/useSubmitForm';
import {
  CompanyConfigurationFinalizationProcess,
  CompanyConfigurationFinalizationProcessVariables,
} from './__generated__/CompanyConfigurationFinalizationProcess';
import { EditRuleView } from './EditRuleView';
import { Header } from './Header';
import { EditRuleViewValues, NewRuleInput } from './types';
import { getDefaultValues } from './utils';
import { CompanyConfigurationWarning } from '../__generated__/CompanyConfigurationWarning';
import { ConfirmDialog } from 'common/components/ConfirmDialog';
import { expandedNav } from 'utility/cache/ui';

const CONFIRM_FINALIZE_DIALOG_TITLE = 'Are you sure?';
const CONFIRM_FINALIZE_DIALOG_TEXT =
  'Please confirm that you wish to perform finalization processes for :';

export interface SelectedCategoryRulesViewProps {
  selectedNode: CompanyConfigurationCategories_companyConfigurationCategories_nodes;
  businessRuleCategoriesOptions: IDropdownOption<any>[];
  onDirtyChange?: (isDirty: boolean, values: EditRuleViewValues) => void;
  fetchCompanyConfigurtionWarning: LazyQueryExecFunction<
    CompanyConfigurationWarning,
    OperationVariables
  >;
  companyConfigurationWarningData: CompanyConfigurationWarning | undefined;
}

export const SelectedCategoryRulesView: React.FC<
  SelectedCategoryRulesViewProps
> = ({
  selectedNode,
  businessRuleCategoriesOptions,
  onDirtyChange,
  fetchCompanyConfigurtionWarning,
  companyConfigurationWarningData,
}) => {
  const theme = useTheme();
  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const [showMessage, setShowMessage] = useState(false);
  const [confirmFinalize, setConfirmFinalize] = useState<boolean>(false);
  const [finalizeProcess, { data, loading: finalizeLoading }] = useMutation<
    CompanyConfigurationFinalizationProcess,
    CompanyConfigurationFinalizationProcessVariables
  >(FINALIZE_PROCESS, { errorPolicy: 'all' });
  const formMethods = useForm<EditRuleViewValues>({
    mode: 'all',
    defaultValues: { rules: [] },
  });

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { isDirty },
  } = formMethods;
  const { fields, append, remove } = useFieldArray({ control, name: 'rules' });
  const isExpandedNav = useReactiveVar(expandedNav);
  const onFinalizeProcess = async () => {
    setConfirmFinalize(false);
    const { errors } = await finalizeProcess({
      variables: {
        input: {
          configurationGroupItemsId: selectedNode.id!,
        },
      },
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: BUSINESS_CATEGORY_REFRESH,
          variables: { id: selectedNode.id },
        },
      ],
    });

    if (errors?.length) {
      cutToast.error(errors[0].message);
    } else {
      fetchCompanyConfigurtionWarning();
      cutToast.success('Settings finalized successfully');
    }
  };
  const { submitForm, ruleUpdateLoading } = useSubmitForm();

  const onHandleSubmit = async (values: EditRuleViewValues) => {
    if (values && selectedNode) await submitForm(values, selectedNode);
    fetchCompanyConfigurtionWarning();
  };

  useEffect(() => {
    if (selectedNode?.companyBusinessRulesByConfigurationGroupItemId?.nodes) {
      reset({ rules: getDefaultValues(selectedNode) });
    }
  }, [selectedNode, reset]);

  const handleAddRule = (newRule: NewRuleInput) => {
    append({
      id: null,
      _rowTimestamp: null,
      layoutType: selectedNode?.additionsBusinessRuleLayoutType ?? null,
      configurationCategoryId: selectedNode?.configurationCategoryId ?? null,
      businessRuleTypeId: newRule.businessRuleTypeId ?? 0,
      _isConfigEditingAllowed: false,
      isCategorySwitchingAllowed: false,
      name: selectedNode?.additionsBusinessRuleName,
      sortOrder: selectedNode?.sortOrder,
      _isUpdatable: true,
      _isDeletable: true,
      ...newRule,
    });
  };

  useEffect(() => {
    onDirtyChange?.(isDirty, watch());
  }, [isDirty]);

  useEffect(() => {
    fetchCompanyConfigurtionWarning();
  }, []);

  useEffect(() => {
    setShowMessage(
      !!companyConfigurationWarningData?.companyConfigurationWarning?.length
    );
  }, [companyConfigurationWarningData]);

  return (
    <FormProvider {...formMethods}>
      <Stack
        style={{
          width: isExpandedNav ? 'calc(100vw - 830px)' : 'calc(100vw - 600px )',
          marginRight: 4,
        }}
        tokens={{ childrenGap: 50 }}
      >
        {showMessage && (
          <MessageBar
            messageBarType={MessageBarType.severeWarning}
            onDismiss={() => setShowMessage(false)}
            styles={{
              root: {
                display: 'inline-flex',
                width: 'fit-content',
                maxWidth: '100%',
                maxHeight: 42,
              },
              content: {
                overflow: 'hidden',
              },
            }}
          >
            {companyConfigurationWarningData?.companyConfigurationWarning}
          </MessageBar>
        )}
        <Stack
          horizontal
          verticalAlign="center"
          tokens={{ childrenGap: 10 }}
          style={{
            backgroundColor: theme.palette.neutralLighterAlt,
          }}
        >
          <Stack
            horizontalAlign="center"
            grow
            style={{
              width: 'auto',
              marginRight: 4,
              padding: '10px 0px',
            }}
          >
            <Text
              style={{ color: theme.palette.themePrimary }}
              variant="xLargePlus"
            >
              {selectedNode?.name}
            </Text>
          </Stack>
          <Stack
            horizontal
            tokens={{ childrenGap: 10 }}
            style={{ marginLeft: 'auto', paddingRight: 48 }}
          >
            {selectedNode?.isAdditionsAllowed && (
              <PrimaryButton
                onClick={() => setShowAddModal(true)}
                style={{ width: 150 }}
              >
                Add New Rule
              </PrimaryButton>
            )}

            {selectedNode?.isFinalizationProcessRequired && (
              <PrimaryButton
                onClick={() => setConfirmFinalize(true)}
                style={{ width: 150 }}
                disabled={finalizeLoading}
              >
                Finalize Settings
              </PrimaryButton>
            )}
          </Stack>
        </Stack>
        <Stack tokens={{ childrenGap: 50, padding: '0px 20px' }}>
          <Header />
          <Stack
            tokens={{ childrenGap: 15 }}
            style={{
              overflow: 'auto',
              height: showMessage
                ? 'calc(100vh - 460px)'
                : 'calc(100vh - 380px)',
            }}
          >
            {fields.map((field, index) => (
              <Stack key={field.id}>
                <Stack horizontal tokens={{ childrenGap: 50 }}>
                  <EditRuleView
                    control={control}
                    index={index}
                    field={field}
                    remove={remove}
                    businessRuleCategoriesOptions={
                      businessRuleCategoriesOptions
                    }
                  />
                </Stack>
                <Separator />
              </Stack>
            ))}
            {showAddModal && selectedNode?.additionsBusinessRuleLayoutType && (
              <AddRuleModal
                onClose={() => setShowAddModal(false)}
                onAdd={handleAddRule}
                selectedNode={selectedNode}
              />
            )}
          </Stack>
          <Stack
            horizontal
            horizontalAlign="end"
            style={{ width: 200, marginLeft: 'auto', marginRight: 20 }}
            tokens={{ childrenGap: 20 }}
          >
            <PrimaryButton
              text="Save"
              onClick={handleSubmit(onHandleSubmit)}
              disabled={!isDirty || ruleUpdateLoading}
            />
            <DefaultButton
              text="Cancel"
              onClick={() => {
                reset({ rules: getDefaultValues(selectedNode) });
              }}
            />
          </Stack>
        </Stack>
        <ConfirmDialog
          hidden={!confirmFinalize}
          title={CONFIRM_FINALIZE_DIALOG_TITLE}
          onDismiss={() => setConfirmFinalize(false)}
          onConfirm={onFinalizeProcess}
        >
          <Stack
            horizontalAlign="center"
            tokens={{ childrenGap: 10 }}
            style={{ width: 300, marginBottom: 30 }}
          >
            <Text>{CONFIRM_FINALIZE_DIALOG_TEXT}</Text>
            <Text
              style={{ color: theme.palette.themePrimary }}
              variant="xLarge"
            >
              {selectedNode?.name}
            </Text>
          </Stack>
        </ConfirmDialog>
        <Prompt
          when={isDirty}
          message="Are you sure you want to leave your changes unsaved?"
        />
      </Stack>
    </FormProvider>
  );
};
